// src/components/Auth/LoginForm.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Divider,
  Stack
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  School as SchoolIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import LegalFooter from '../common/LegalFooter';

const LoginForm = () => {
  const [cin, setCin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const { login, isLoading, error, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    if (!cin.trim() || !password.trim()) return;

    const result = await login(cin.trim().toUpperCase(), password.trim(), requires2FA ? totpCode.trim() : undefined);
    if (result?.requires2FA) {
      setRequires2FA(true);
    } else if (result?.success) {
      navigate('/dashboard');
    }
    // result.success === false is handled by AuthContext which sets the error state
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'fr' : 'ar';
    localStorage.setItem('language', newLang);
    window.location.reload();
  };



  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        // Removed `direction` from here, as it's set globally in main.jsx ThemeProvider
        // Setting it here can cause conflicts with MUI's RTL handling via emotion cache
      }}
    >
      {/* You previously removed Container. Let's ensure proper centering without it. */}
      {/* The Paper component itself needs to be horizontally centered within the flex Box. */}
      <Paper
        elevation={24}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          width: '100%',
          maxWidth: '500px', // Example fixed width for the form container
          margin: 'auto', // <--- ADD THIS LINE for horizontal centering
          // Ensure specific overrides for RTL direction on text alignment if needed,
          // but MUI usually handles this with the `direction: 'rtl'` on the theme.
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
            color: 'white',
            padding: 4,
            textAlign: 'center'
          }}
        >
          <SchoolIcon sx={{ fontSize: 48, mb: 2 }} />

          <Typography variant="h6" opacity={0.9}>
            {t('studentSystem')}
          </Typography>
        </Box>

        <CardContent sx={{ padding: 4 }}>
          {/* Language Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Button
              onClick={toggleLanguage}
              variant="outlined"
              size="small"
              sx={{ borderRadius: 3 }}
            >
              {i18n.language === 'ar' ? 'Français' : 'العربية'}
            </Button>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label={t('cinLabel')}
                placeholder={t('enterCin')}
                value={cin}
                onChange={(e) => setCin(e.target.value)}
                required
                disabled={isLoading}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label={t('passwordLabel')}
                placeholder={i18n.language === 'ar' ? 'تاريخ الازدياد أو كلمة المرور' : 'Date de naissance ou mot de passe'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                helperText={
                  i18n.language === 'ar'
                    ?'كلمة المرور الافتراضية هي تاريخ الازدياد (JJ/MM/AAAA). إذا قمت بتغييرها، أدخل كلمة المرور الجديدة.'
                    : 'Par défaut : date de naissance (JJ/MM/AAAA) — si vous avez changé votre mot de passe, entrez le nouveau'
                }
                InputProps={{
                  startAdornment: <LockIcon sx={{ mr: 1, color: 'primary.main' }} />,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(v => !v)} edge="end" tabIndex={-1}>
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              {requires2FA && (
                <TextField
                  fullWidth
                  label="Code d'authentification (6 chiffres)"
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  disabled={isLoading}
                  inputProps={{ maxLength: 6, style: { letterSpacing: 8, fontSize: 22, textAlign: 'center' } }}
                  helperText="Ouvrez votre application d'authentification et entrez le code"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading || !cin.trim() || !password.trim() || (requires2FA && totpCode.length !== 6)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                  boxShadow: '0 4px 15px rgba(52, 152, 219, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2980b9 0%, #3498db 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(52, 152, 219, 0.4)'
                  }
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('loginButton')
                )}
              </Button>
            </Stack>
          </form>

          {/* Demo Section */}
       

          {/* Footer */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {i18n.language === 'ar' ? (
                <>
                  <strong>N° Apogée</strong> + تاريخ الازدياد (JJ/MM/AAAA)<br />
                
                </>
              ) : (
                <>
                  <strong>N° Apogée</strong> + date de naissance (JJ/MM/AAAA)<br />
                  <Box component="span" sx={{ color: '#e67e22', fontSize: '0.82rem' }}>
                    Si vous avez déjà modifié votre mot de passe, saisissez le nouveau
                  </Box>
                </>
              )}
            </Typography>
            {/* Copyright */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #f0f0f0' }}>
              <Typography variant="caption" color="text.disabled" display="block">
                © {new Date().getFullYear()}{' '}
                {i18n.language === 'ar'
                  ? 'كلية العلوم القانونية والسياسية — سطات'
                  : 'Faculté des Sciences Juridiques et Politiques — Settat'}
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                {i18n.language === 'ar' ? 'جميع الحقوق محفوظة' : 'Tous droits réservés'}
                {' — '}
                <Box component="span" sx={{ color: '#e67e22', fontWeight: 700, fontSize: 'inherit' }}>
                  Version Bêta
                </Box>
              </Typography>
              <LegalFooter />
            </Box>
          </Box>
        </CardContent>
      </Paper>
    </Box>
  );
};

export default LoginForm;